import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Player } from '@core/types';
import { getPlayers, getSettings } from '../../storage';
import { importLocal } from '../../api/peladas';
import { buttonClass } from '../../components/ui';

const DISMISSED_KEY = 'balancedsquad.importDismissed';

// Oferta de subir o elenco que já está no navegador. É o gancho de conversão do
// §7: quem cadastrou a galera sem conta não redigita nada.
//
// Some sozinho quando não há elenco local, e fica dispensável — mas a dispensa
// é lembrada, senão a oferta reapareceria a cada visita como um banner chato.
export default function ImportLocalCard({ onImported }: { onImported: () => void }) {
  const { t } = useTranslation();
  const [local, setLocal] = useState<Player[] | null>(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(DISMISSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    void getPlayers().then(setLocal);
  }, []);

  if (dismissed || local === null || local.length === 0) return null;

  function dismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISSED_KEY, '1');
    } catch {
      // Sem persistência a oferta volta na próxima visita; não é motivo para
      // impedir a dispensa agora.
    }
  }

  async function handleImport() {
    if (!local) return;

    setBusy(true);
    setError(null);

    const settings = await getSettings();
    const result = await importLocal({
      name: name.trim() || t('cloud.importDefaultName'),
      playersPerTeam: settings.playersPerTeam,
      players: local.map((p) => ({ name: p.name, level: p.level, gender: p.gender ?? null })),
    });

    setBusy(false);

    if (result.ok) {
      // Não apaga o elenco local: se algo der errado do lado da nuvem, a
      // pessoa ainda tem os dados. Só para de oferecer.
      dismiss();
      onImported();
    } else {
      setError(result.error);
    }
  }

  return (
    <section className="flex flex-col gap-3 rounded-xl border border-pitch-950 bg-chalk-100 p-5 dark:border-chalk-300 dark:bg-pitch-800">
      <div>
        <h2 className="font-semibold">{t('cloud.importTitle')}</h2>
        <p className="mt-1 text-sm text-chalk-600 dark:text-chalk-300">
          {t('cloud.importBody', { count: local.length })}
        </p>
      </div>

      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={t('cloud.importDefaultName')}
        aria-label={t('cloud.newPelada')}
        className="rounded-lg border border-chalk-200 bg-white px-3 py-2 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-focus dark:border-pitch-600 dark:bg-pitch-950"
      />

      {error && (
        <p role="alert" className="text-sm text-signal-danger dark:text-signal-danger">
          {t('cloud.errorGeneric')}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void handleImport()}
          disabled={busy}
          aria-busy={busy}
          className={buttonClass()}
        >
          {busy ? t('common.sending') : t('cloud.importCta')}
        </button>
        <button
          type="button"
          onClick={dismiss}
          className="rounded-lg border border-chalk-300 px-4 py-2 text-sm font-semibold transition-colors hover:bg-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-signal-focus dark:border-pitch-600 dark:hover:bg-pitch-800"
        >
          {t('cloud.importDismiss')}
        </button>
      </div>
    </section>
  );
}
